/**
 * LockHeadings - a Tiptap extension that makes a known set of structural section headings
 * READ-ONLY in the editor, via a ProseMirror `filterTransaction` (it rejects any user
 * transaction that would change a locked heading's text). Nothing is persisted differently;
 * it only prevents edits.
 *
 * Why: in the deal-SOW editor the narrative body is split + keyed by `slugifyHeading(heading)`
 * (see `resolve.ts`), and the View renders each section's heading from the CONTRACT, not from
 * the editor. So editing a section heading (1) re-slugs its body -> the `contentBlock` for the
 * original slug resolves empty -> the body silently DISAPPEARS, and (2) has no visible effect
 * (the template heading still renders). Those headings are template structure; locking them
 * removes the only way to accidentally orphan a section's content. The host passes the current
 * set of heading texts to lock via `getLocked` (read live through a ref).
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
const lockKey = new PluginKey('lockHeadings');
/** System-tooltip text shown on a locked section heading (native `title`). */
export const LOCKED_HEADING_TOOLTIP = 'This section heading is set by the SOW template and cannot be edited here. Edit the section text below it instead.';
/** Count, per heading text, how many headings in `doc` carry a locked text. */
function lockedHeadingCounts(doc, locked) {
    const counts = new Map();
    doc.descendants((node) => {
        if (node.type.name !== 'heading')
            return;
        const t = node.textContent.trim();
        if (locked.has(t))
            counts.set(t, (counts.get(t) ?? 0) + 1);
    });
    return counts;
}
/**
 * A transaction edits a locked heading iff any locked heading text present BEFORE is no longer
 * present (same count) AFTER. This compares the actual rendered heading texts (via `tr.doc`,
 * the post-transaction document) rather than analysing step ranges - so it correctly catches
 * insertions at the heading's very end, mid-text edits, and whole-heading deletes, while
 * allowing edits that DON'T change a heading's text: typing in the body, pressing Enter after a
 * heading (adds a block, heading text unchanged), and the host's programmatic `setContent`
 * (the reloaded doc still contains every locked heading, so no count drops).
 */
function editsALockedHeading(tr, state, locked) {
    if (!tr.docChanged || locked.size === 0)
        return false;
    const before = lockedHeadingCounts(state.doc, locked);
    if (before.size === 0)
        return false;
    const after = lockedHeadingCounts(tr.doc, locked);
    for (const [text, count] of before) {
        if ((after.get(text) ?? 0) < count)
            return true; // a locked heading lost its exact text
    }
    return false;
}
export const LockHeadings = Extension.create({
    name: 'lockHeadings',
    addOptions() {
        return { getLocked: () => new Set() };
    },
    addProseMirrorPlugins() {
        const getLocked = () => this.options.getLocked();
        return [
            new Plugin({
                key: lockKey,
                filterTransaction: (tr, state) => !editsALockedHeading(tr, state, getLocked()),
                props: {
                    // Mark locked headings read-only: a lock icon (CSS) + the system tooltip (native
                    // `title`) explaining why, and a default cursor.
                    decorations: (state) => {
                        const locked = getLocked();
                        if (locked.size === 0)
                            return DecorationSet.empty;
                        const decos = [];
                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'heading' && locked.has(node.textContent.trim())) {
                                decos.push(Decoration.node(pos, pos + node.nodeSize, {
                                    class: 'rte-heading--locked',
                                    // `data-tooltip` drives a styled CSS tooltip (matching `.ui-tooltip`),
                                    // not the unstyled native `title`.
                                    'data-tooltip': LOCKED_HEADING_TOOLTIP,
                                }));
                            }
                        });
                        return DecorationSet.create(state.doc, decos);
                    },
                },
            }),
        ];
    },
});
//# sourceMappingURL=lockHeadings.js.map